import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@storable/canaveral-components';
import {
  FormGroup,
  Form,
  Row,
  Col,
} from 'react-bootstrap';
import { useForm, Controller, useWatch } from 'react-hook-form';
import Button from '../../../../modules/react/components/stateless/Button';
import Select from '../../../../modules/react/components/stateful/Select';
import styles from './styles.scss';
import switchStyles from '../../../../src/web/components/form/inputs/Switch/Switch.scss';
import {
  vimeoParser,
  youtubeParser,
  parseIFrame,
  detectVideoSource,
} from '../../../../src/web/utils/videoUtils';
import FormFieldLabel from '../../../../admin/components/FormFieldLabel';

const ASPECT_RATIO_OPTIONS = [
  { label: '16:9 - Widescreen', value: 16 / 9, height: 9, width: 16 },
  { label: '4:3 - Standard', value: 4 / 3, height: 3, width: 4 },
  { label: '1:1 - Square', value: 1, height: 1, width: 1 },
  { label: 'Custom', value: 0, height: 0, width: 0 },
];

const getAspectValue = (contentWidth, contentHeight) => {
  switch (contentWidth / contentHeight) {
    default:
      return 0;
    case 16 / 9:
    case 4 / 3:
    case 1:
      return contentWidth / contentHeight;
  }
};

const VideoEditor = (props) => {
  const {
    onCancelEdit,
    onFinishEdit,
    url,
    videoSource,
    autoPlay,
    loop,
    mute,
    controls,
    altText,
    title,
    contentHeight,
    contentWidth,
  } = props;
  const [aspectRatio, setAspectRatio] = useState(16 / 9);

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      autoPlay: autoPlay ?? false,
      mute: mute ?? false,
      loop: loop ?? false,
      controls: controls ?? false,
      altText: altText ?? '',
      title: title ?? '',
      url: url ?? '',
      videoSource: videoSource ?? 'youtube',
      contentHeight: contentHeight ?? 9,
      contentWidth: contentWidth ?? 16,
    },
  });

  useEffect(() => {
    setAspectRatio(getAspectValue(contentWidth, contentHeight));
  }, [contentHeight, contentWidth]);

  const allValues = useWatch({ control });

  const onSubmitData = (videoWidgetData) => {
    onFinishEdit({ ...videoWidgetData, videoSource: detectVideoSource(videoWidgetData.url) });
  };
  return (
    <Modal show onDismiss={onCancelEdit} aria-labelledby="stock-icons-editor">
      <form onSubmit={handleSubmit(onSubmitData)}>
        <Modal.Header>
          <Modal.Title>YouTube/Vimeo Video Widget</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.root}>
          <Form.Group>
            <Form.Label>Quick Start - Paste Your Video Embed Code</Form.Label>
            <Form.Text className={styles.blocktext}>
              Paste an embed code from YouTube or Vimeo to configure the video
              settings below. Alternatively you can configure these settings
              manually.
            </Form.Text>
            <Form.Control
              name="embedCode"
              as="textarea"
              rows={4}
              placeholder="Embed Code"
              onChange={(ev) => {
                const { value } = ev.target;
                const videoData = parseIFrame(value);
                if (videoData) {
                  const attainedContentHeight = videoData.height || 9;
                  const attainedContentWidth = videoData.width || 16;
                  setValue('url', videoData.src);
                  setValue('contentHeight', attainedContentHeight);
                  setValue('contentWidth', attainedContentWidth);
                  setValue('title', videoData.title);
                  setValue('altText', videoData.title);
                  setValue('videoSource', videoData.videoSource);
                  setAspectRatio(
                    getAspectValue(attainedContentWidth, attainedContentHeight),
                  );
                }
              }}
            />
          </Form.Group>

          <div className={styles.dividerLine} />

          <Controller
            name="url"
            rules={{
              validate: {
                pattern: value => (/(([\w]+:)?\/\/)?(([\d\w]|%[a-fA-f\d]{2,2})+(:([\d\w]|%[a-fA-f\d]{2,2})+)?@)?([\d\w][-\d\w]{0,253}[\d\w]\.)+[\w]{2,63}(:[\d]+)?(\/([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)*(\?(&?([-+_~.\d\w]|%[a-fA-f\d]{2,2})=?)*)?(#([-+_~.\d\w]|%[a-fA-f\d]{2,2})*)?/.test(
                  value,
                )
                  ? true
                  : 'Please add a valid URL'),
                valid: (value) => {
                  if (detectVideoSource(value) === 'youtube') {
                    return youtubeParser(value)
                      ? true
                      : 'Please provide a valid YouTube URL';
                  }
                  if (detectVideoSource(value) === 'vimeo') {
                    return vimeoParser(value)
                      ? true
                      : 'Please provide a valid Vimeo URL';
                  }
                  if (detectVideoSource(value) === 'unknown') {
                    return 'Please provide a valid YouTube or Vimeo URL';
                  }
                  return true;
                },
              },
              required:
                'A video URL is required. Please provide a video URL from YouTube or Vimeo.',
            }}
            control={control}
            render={({
              field: { name, onBlur, onChange, ref, value },
              formState: { errors },
            }) => (
              <FormGroup className={styles.formGroup} validationState={errors.url ? 'error' : null}>
                <FormFieldLabel
                  htmlFor={name}
                  title="Video URL"
                  required
                  tooltip="Add a URL from YouTube or Vimeo to display your video. We recommend using the embed URL to pre-populate the fields below."
                />
                <Form.Control
                  placeholder="Video URL"
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  ref={ref}
                  className={errors.url?.message ? styles.inputError : ''}
                />
                {errors.url?.message && (
        <span style={{ color: '#a94442', marginTop: 20 }} role="alert">{errors.url?.message ?? ''}</span>
                )}
              </FormGroup>
            )}
          />
          <Controller
            name="altText"
            rules={{
              required: 'This field is required',
            }}
            control={control}
            render={({
              field: { name, onBlur, onChange, ref, value },
              formState: { errors },
            }) => (
              <FormGroup className={styles.formGroup} validationState={errors.altText ? 'error' : null}>
                <FormFieldLabel
                  htmlFor={name}
                  title="Alt Text"
                  required
                  tooltip="Alt text helps search engines better crawl and rank your website and also allows screen-reading tools to describe images and videos to visually impaired readers."
                />
                <Form.Control
                  placeholder="Alt Text"
                  name={name}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                  ref={ref}
                  className={errors.url?.message ? styles.inputError : ''}
                />
                       {errors.url?.message && (
        <span style={{ color: '#a94442', marginTop: 20 }} role="alert">{errors.altText?.message ?? ''}</span>
                       )}
              </FormGroup>
            )}
          />
          <Controller
            name="title"
            control={control}
            render={({ field }) => (
              <FormGroup className={styles.formGroup}>
                <FormFieldLabel
                  htmlFor={field.name}
                  title="Title Text"
                  tooltip="Title text is used to provide additional information about the video."
                />
                <Form.Control placeholder="Title Text" {...field} />
              </FormGroup>
            )}
          />

          <FormGroup className={styles.formGroup}>
            <FormFieldLabel
              htmlFor="aspectRatio"
              title="Aspect Ratio"
              tooltip="Aspect ratio is the proportional relationship between the width of a video image compared to its height. It is usually expressed as width:height (separated by a colon), such as 16:9 or 4:3. The aspect ratio sets how wide a video is formatted and affects how it will fit on your viewing screen."
            />
            <Select
              canClear={false}
              name="aspectRatio"
              value={aspectRatio}
              options={ASPECT_RATIO_OPTIONS}
              onChange={(ev) => {
                setAspectRatio(ev.value);
                setValue('contentHeight', ev.height);
                setValue('contentWidth', ev.width);
              }}
            />
          </FormGroup>
          {aspectRatio === 0 && (
            <Row>
              <Col xs={6}>
                <Controller
                  name="contentWidth"
                  control={control}
                  render={({
                    field: { name, onBlur, onChange, ref, value },
                  }) => (
                    <FormGroup>
                      <FormFieldLabel
                        htmlFor={name}
                        title="Width"
                        tooltip="Provide the native width of your video to define the aspect ratio"
                      />
                      <Form.Control
                        type="number"
                        placeholder="Content Width"
                        name={name}
                        onBlur={onBlur}
                        onChange={ev => onChange(parseInt(ev.target.value, 10))
                        }
                        value={value}
                        inputRef={ref}
                      />
                    </FormGroup>
                  )}
                />
              </Col>
              <Col xs={6}>
                <Controller
                  name="contentHeight"
                  control={control}
                  render={({
                    field: { name, onBlur, onChange, ref, value },
                  }) => (
                    <FormGroup>
                      <FormFieldLabel
                        htmlFor={name}
                        title="Height"
                        tooltip="Provide the native height of your video to define the aspect ratio"
                      />
                      <Form.Control
                        type="number"
                        placeholder="Content Height"
                        name={name}
                        onBlur={onBlur}
                        onChange={ev => onChange(parseInt(ev.target.value, 10))
                        }
                        value={value}
                        inputRef={ref}
                      />
                    </FormGroup>
                  )}
                />
              </Col>
            </Row>
          )}
          <Controller
            name="autoPlay"
            control={control}
            render={({ field: { name, value, onChange, onBlur } }) => (
              <FormGroup className={styles.formGroup}>
                <label className={switchStyles.switch}>
                  <input
                    type="checkbox"
                    name={name}
                    checked={value}
                    onChange={ev => onChange(ev.target.checked)}
                    onBlur={onBlur}
                  />
                  <span className={switchStyles.slider} />
                </label>
                <Form.Label>
                  <span className={styles.headerStyle}>Enable Autoplay</span>
                </Form.Label>
              </FormGroup>
            )}
          />
          <Controller
            name="mute"
            control={control}
            render={({ field: { name, value, onChange, onBlur } }) => (
              <FormGroup className={styles.formGroup}>
                <label className={switchStyles.switch}>
                  <input
                    disabled={allValues.autoPlay}
                    type="checkbox"
                    name={name}
                    checked={allValues.autoPlay ? true : value}
                    onChange={ev => onChange(ev.target.checked)}
                    onBlur={onBlur}
                  />
                  <span className={switchStyles.slider} />
                </label>
                <Form.Label>
                  <span className={styles.headerStyle}>Mute Video</span>
                </Form.Label>
              </FormGroup>
            )}
          />
          <Controller
            name="loop"
            control={control}
            render={({ field: { name, value, onChange, onBlur } }) => (
              <FormGroup className={styles.formGroup}>
                <label className={switchStyles.switch}>
                  <input
                    type="checkbox"
                    name={name}
                    checked={value}
                    onChange={ev => onChange(ev.target.checked)}
                    onBlur={onBlur}
                  />
                  <span className={switchStyles.slider} />
                </label>
                <Form.Label>
                  <span className={styles.headerStyle}>Loop Video</span>
                </Form.Label>
              </FormGroup>
            )}
          />
          <Controller
            name="controls"
            control={control}
            render={({ field: { name, value, onChange, onBlur } }) => (
              <FormGroup>
                <label className={switchStyles.switch}>
                  <input
                    type="checkbox"
                    name={name}
                    checked={value}
                    onChange={ev => onChange(ev.target.checked)}
                    onBlur={onBlur}
                  />
                  <span className={switchStyles.slider} />
                </label>
                <Form.Label>
                  <span className={styles.headerStyle}>
                    Enable Video Player Controls
                  </span>
                </Form.Label>
              </FormGroup>
            )}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="default" onClick={onCancelEdit}>Cancel</Button>
          <Button type="submit" variant="success">
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

VideoEditor.propTypes = {
  onCancelEdit: PropTypes.func.isRequired,
  onFinishEdit: PropTypes.func.isRequired,
  url: PropTypes.string.isRequired,
  videoSource: PropTypes.string.isRequired,
  autoPlay: PropTypes.bool,
  loop: PropTypes.bool,
  mute: PropTypes.bool,
  controls: PropTypes.bool,
  altText: PropTypes.string,
  title: PropTypes.string,
  contentHeight: PropTypes.number,
  contentWidth: PropTypes.number,
};
VideoEditor.defaultProps = {
  autoPlay: false,
  loop: false,
  mute: false,
  controls: false,
  altText: '',
  title: '',
  contentHeight: 9,
  contentWidth: 16,
};

export default VideoEditor;
